import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ImCheckmark, ImCross } from "react-icons/im";

import { UseOutletContextJournal } from './Journal';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { BookingJournalDA } from '../../../../services/admin/booking.journal.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { CompanyBookingJournals } from '../../../../models/company.booking.journals.model';
import { BookingJournal } from '../../../../models/booking.journal.model';
import { modalShow } from '../../../../utils/modal.util';
import { getError } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { journalTypeValue } from '../../../../libraries/journal.type.library';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { evaluateLegendRequiredIcon } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { legendInvalid, legendIconValidInvalidRestart } from '../../../../utils/legend.data.util';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppPreloader from '../../../../components/Preloader';
import AppSortTable from '../../../../components/element/SortTable';
import AppLegend from '../../../../components/element/Legend';
import AppModalBookingJournalCreate from '../../../../components/modal/ModalBookingJournalCreate';

export interface JournalListPageProps {};

let errorResponse: Error, bookingJournalResponse: BookingJournal, bookingJournalsResponse: CompanyBookingJournals;

const JournalListPage: React.FunctionComponent<JournalListPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextJournal()
  const {lang} = StateLanguage()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [filter, setFilter] = useState({search: '', options: {sort_field: '', sort_mode: ''}})
  const [buttons, setButtons] = useState<boolean[] | null>(null)
  const [bookingJournals, setBookingJournals] = useState<BookingJournal[] | null>(null)

  const loadBookingJournals = async (id_company: string) => {
    await BookingJournalDA.getBookingJournals(id_company, filter.search, filter.options.sort_field, filter.options.sort_mode).then( (response) => {
      if (response.status === 200) {
        const raw = response.data
        let rawList: any[]
        if (Array.isArray(raw)) {
          rawList = raw
        } else if (raw?.items && Array.isArray(raw.items)) {
          rawList = raw.items
        } else if (raw?.booking_journals) {
          rawList = raw.booking_journals
        } else {
          rawList = []
        }
        const journals: BookingJournal[] = rawList.map((j: any) => ({...j, created_at: j.created_at ?? j.createdAt, updated_at: j.updated_at ?? j.updatedAt}))

        setButtons(journals.map(() => false))
        setBookingJournals(journals)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitBookingJournalCreate = async (id_booking_journal: string) => {
    setLoadIndicator('on')

    if (companyForUser && bookingJournals && name.valid) {
      await BookingJournalDA.putBookingJournal(companyForUser.id, id_booking_journal, name.value).then( (response) => {
        if (response.status === 200) {
          const rawJournal = response.data?.booking_journal ?? response.data
          bookingJournalResponse = {...rawJournal, created_at: rawJournal.created_at ?? rawJournal.createdAt, updated_at: rawJournal.updated_at ?? rawJournal.updatedAt}

          let tempBookingJournals = bookingJournals.map((temp_item) => {
            if (id_booking_journal === temp_item.id) {
              temp_item = bookingJournalResponse
            }
            return temp_item
          })

          setBookingJournals(tempBookingJournals)

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedBookingJournal,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            setButtons(tempBookingJournals.map(() => false))
            restartInput()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value) legendInvalid('legend-name-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeSearch = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (companyForUser) {
      filter.search = event.target.value
      filter.options.sort_field= ''
      filter.options.sort_mode = ''
      loadBookingJournals(companyForUser.id)
    }
  }

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.name, name, setName, 'input-name', 'legend-name-valid', 'legend-name-required')
  }

  const executeShowModalBookingJournalCreate = () => {
    modalShow('modal-booking-journal-create')
  }

  const executeButtonEditSelect = (index: number) => {
    if (buttons) {
      let tempButtons = buttons.map((temp_item, temp_index) => {
        if (index === temp_index) {
          temp_item = !temp_item
        } else {
          temp_item = false
        }
        return temp_item
      })

      setButtons(tempButtons)
      restartInput()
    }
  }

  const executeButtonEditReset = (index: number) => {
    if (buttons) {
      let tempButtons = buttons.map((temp_item, temp_index) => {
        if (index === temp_index) {
          temp_item = false
        }
        return temp_item
      })

      setButtons(tempButtons)
      restartInput()
    }
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      sortColumn(item, filter.options)
      loadBookingJournals(companyForUser.id)
    }
  }

  function restartInput() {
    legendIconValidInvalidRestart('input-name', 'legend-name-valid')
    legendIconValidInvalidRestart('input-name', 'legend-name-required')

    setName({value: '', valid: false})
  }

  useEffect( () => {
    if (companyForUser) {
      filter.search = ''
      filter.options.sort_field = ''
      filter.options.sort_mode = ''

      setBookingJournals(null)
      loadBookingJournals(companyForUser.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: lang.labels.listBookingJournals}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && bookingJournals && buttons
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <div className="d-flex align-items-center position-relative">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                    <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                  </svg>
                </span>
                <input className="form-control w-350px ps-14" type="text" autoComplete="off" placeholder={lang.labels.searchBookingJournal} value={filter.search} onChange={handleChangeSearch} />
              </div>
            </div>
            <div className="card-toolbar">
              <div className="d-flex justify-content-end align-items-center">
                <button className="btn btn-primary" type="button" onClick={executeShowModalBookingJournalCreate}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                        <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                        <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.addBookingJournal}
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-300px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("name")}>
                          <span className="me-1">{lang.labels.name}</span>
                          <AppSortTable attribute={"name"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("create")}>
                          <span className="me-1">{lang.labels.createdAt}</span>
                          <AppSortTable attribute={"create"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("update")}>
                          <span className="me-1">{lang.labels.updatedAt}</span>
                          <AppSortTable attribute={"update"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { bookingJournals.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      <tr className="row-dashed">
                        <td colSpan={4}>
                          <div className="d-flex justify-content-start align-items-center">
                            <div className="d-flex flex-stack">
                              <Link to={"/main/accounting/journal/view/all"} className="text-hover-primary">
                                <span className="svg-icon svg-icon-2tx svg-icon-dark me-3">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM15 17C15 16.4 14.6 16 14 16H8C7.4 16 7 16.4 7 17C7 17.6 7.4 18 8 18H14C14.6 18 15 17.6 15 17ZM17 12C17 11.4 16.6 11 16 11H8C7.4 11 7 11.4 7 12C7 12.6 7.4 13 8 13H16C16.6 13 17 12.6 17 12ZM17 7C17 6.4 16.6 6 16 6H8C7.4 6 7 6.4 7 7C7 7.6 7.4 8 8 8H16C16.6 8 17 7.6 17 7Z" fill="black"/>
                                    <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                                  </svg>
                                </span>
                              </Link>
                              <div className="d-flex flex-column">
                                <Link to={"/main/accounting/journal/view/all"} className="fw-bold text-dark text-hover-primary fs-6">{lang.labels.generalLedger}</Link>
                                <small>{lang.labels.accountingBookWithTotalAccountingEntriesCompany}</small>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                      { bookingJournals.map (( (item, index) => { return (
                        <tr key={index} className="row-dashed">
                          { !buttons[index]
                            ?
                            <>
                              <td>
                                <div className="d-flex flex-column">
                                  <Link to={"/main/accounting/journal/view/" + item.id} className="text-dark fw-bolder text-hover-primary mb-1">{item.name}</Link>
                                  <small className="text-uppercase">{journalTypeValue(lang, item.type)}</small>
                                </div>
                              </td>
                              <td className="fs-7">{dateFormat(lang.code, item.created_at)}</td>
                              <td className="fs-7">{dateFormat(lang.code, item.updated_at)}</td>
                              <td>
                                <div className="d-flex justify-content-end">
                                  <button className="btn btn-sm btn-light btn-active-light-primary w-100" type="button" onClick={() => executeButtonEditSelect(index)}>{lang.labels.edit}</button>
                                </div>
                              </td>
                            </>
                            :
                            <>
                              <td valign="top" colSpan={3}>
                                <input id="input-name" className="form-control" type="text" autoComplete="off" name="name" placeholder={`${journalTypeValue(lang, item.type).toUpperCase()}: ${item.name}`} value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
                                <AppLegend attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                              </td>
                              <td valign="top" className="pt-4">
                                <div className="d-flex justify-content-end">
                                  <button className="btn btn-sm btn-icon btn-light-success me-3" type="button" data-kt-indicator={loadIndicator} onClick={() => submitBookingJournalCreate(item.id)}>
                                    <span className="indicator-label"><ImCheckmark /></span>
                                    <span className="indicator-progress"><span className="spinner-border spinner-border-sm align-middle"></span></span>
                                  </button>
                                  <button className="btn btn-sm btn-icon btn-light-danger" type="button" onClick={() => executeButtonEditReset(index)}>
                                    <ImCross />
                                  </button>
                                </div>
                              </td>
                            </>
                          }
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={4} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalBookingJournalCreate companyForUser={companyForUser} loadBookingJournals={loadBookingJournals}></AppModalBookingJournalCreate>
    </div>
  )
};

export default JournalListPage;
